import React, { useState } from 'react';
import { Input, Select, DatePicker, Button, Segmented } from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  UserOutlined,
  TagsOutlined,
  SelectOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Definitive lists from backend capabilities
const EVENT_TYPES = [
  'USER_LOGIN',
  'USER_LOGOUT',
  'USER_REGISTRATION',
  'TRANSACTION',
  'BILL_PAYMENT',
  'UPDATE_CONFIGURATION',
  'NIN_VERIFICATION_APPROVED',
  'KYC_TIER_DOWNGRADE',
  'KYC_TIER_RESTORED',
  'UPDATE_PROFILE',
  'PASSWORD_CHANGE_SUCCESS',
  'OTP_REQUESTED',
  'OTP_VERIFIED_SUCCESS',
];

const ENTITY_TYPES = [
  'USER',
  'TRANSACTION',
  'BILL',
  'KYC',
  'CONFIGURATION',
  'PRODUCT',
  'LOAN',
  'CARD',
];

const AuditTrailFilters = ({
  filters,
  onFilterChange,
  onApply,
  onReset,
}) => {
  const [activePanel, setActivePanel] = useState(null); // 'search' | 'filter' | null
  const [filterCategory, setFilterCategory] = useState('user'); // 'user' | 'type' | 'entity'

  const handleChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      onFilterChange({
        ...filters,
        startDate: dates[0]?.format('YYYY-MM-DD'),
        endDate: dates[1]?.format('YYYY-MM-DD'),
      });
    } else {
      onFilterChange({ ...filters, startDate: null, endDate: null });
    }
  };

  const handleTogglePanel = (panel) => {
    setActivePanel(activePanel === panel ? null : panel);
  };

  const handleCategoryChange = (val) => {
    setFilterCategory(val);
    // Clear categorical fields when switching to avoid confusing the backend
    onFilterChange({
      ...filters,
      userId: '',
      eventType: null,
      entityType: null,
      entityId: '',
    });
  };

  return (
    <div className="audit-filter-container">
      {/* ── Toggle Bar ────────────────────────────────────────────────────── */}
      <div className="audit-toggle-bar">
        <div className="audit-toggle-left">
          <Button
            className={`audit-toggle-btn ${activePanel === 'search' ? 'active' : ''}`}
            icon={<SearchOutlined />}
            onClick={() => handleTogglePanel('search')}
          >
            Search Logs
          </Button>
          <Button
            className={`audit-toggle-btn ${activePanel === 'filter' ? 'active' : ''}`}
            icon={<FilterOutlined />}
            onClick={() => handleTogglePanel('filter')}
          >
            Detailed Filters
          </Button>
        </div>
        <div className="audit-toggle-right">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => { onReset(); setActivePanel(null); }}
            className="audit-reset-btn"
          >
            Reset
          </Button>
        </div>
      </div>

      {/* ── Search Panel (HITS /search) ────────────────────────────────────── */}
      <div className={`audit-panel-content search-panel ${activePanel === 'search' ? 'expanded' : ''}`}>
        <div className="audit-panel-inner">
          <div className="audit-panel-row">
            <div className="audit-filter-item filter-search">
              <span className="audit-filter-label">Target User ID</span>
              <Input
                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Enter userId..."
                allowClear
                value={filters.userId}
                onChange={(e) => handleChange('userId', e.target.value)}
              />
            </div>

            <div className="audit-filter-item">
              <span className="audit-filter-label">Event Type</span>
              <Select
                placeholder="Event Type"
                allowClear
                showSearch
                value={filters.eventType || undefined}
                onChange={(val) => handleChange('eventType', val)}
              >
                {EVENT_TYPES.map((type) => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
            </div>

            <div className="audit-filter-item">
              <span className="audit-filter-label">Entity Type</span>
              <Select
                placeholder="Entity Type"
                allowClear
                value={filters.entityType || undefined}
                onChange={(val) => handleChange('entityType', val)}
              >
                {ENTITY_TYPES.map((type) => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
            </div>

            <div className="audit-filter-item">
              <span className="audit-filter-label">Status</span>
              <Select
                placeholder="All Statuses"
                allowClear
                value={filters.status || undefined}
                onChange={(val) => handleChange('status', val)}
              >
                <Option value="SUCCESS">Success</Option>
                <Option value="FAILED">Failed</Option>
              </Select>
            </div>

            <div className="audit-filter-item date-range">
              <span className="audit-filter-label">Date Range</span>
              <RangePicker
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
              />
            </div>

            <div className="audit-panel-actions">
              <Button
                type="primary"
                icon={<ArrowRightOutlined />}
                onClick={() => onApply('search')}
                className="audit-panel-apply-btn"
              >
                Search
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filter Panel (HITS Specialized Category Endpoints) ────────────── */}
      <div className={`audit-panel-content filter-panel ${activePanel === 'filter' ? 'expanded' : ''}`}>
        <div className="audit-panel-inner categorical-filter-layout">

          <div className="category-selector-wrapper">
            <span className="audit-filter-label" style={{ marginBottom: 10, display: 'block' }}>
              Select Filter Category
            </span>
            <Segmented
              block
              size="large"
              value={filterCategory}
              onChange={handleCategoryChange}
              options={[
                { label: 'Filter by User', value: 'user', icon: <UserOutlined /> },
                { label: 'Filter by Type', value: 'type', icon: <TagsOutlined /> },
                { label: 'Filter by Entity', value: 'entity', icon: <SelectOutlined /> },
              ]}
              className="audit-category-segmented"
            />
          </div>

          <div className="categorical-groups progressive-disclosure">
            {/* Sequential Display based on selection */}

            {filterCategory === 'user' && (
              <div className="filter-group-sec active-category">
                <div className="group-content">
                  <Input
                    size="large"
                    prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="Enter specific UserId (e.g. 12345)"
                    value={filters.userId}
                    onChange={(e) => handleChange('userId', e.target.value)}
                    style={{ marginBottom: 16 }}
                  />
                  <Button
                    block
                    size="large"
                    type="primary"
                    onClick={() => onApply('user_filter')}
                    disabled={!filters.userId}
                  >
                    Filter By User ID
                  </Button>
                </div>
              </div>
            )}

            {filterCategory === 'type' && (
              <div className="filter-group-sec active-category">
                <div className="group-content">
                  <Select
                    size="large"
                    placeholder="Select Event Type"
                    showSearch
                    allowClear
                    value={filters.eventType || undefined}
                    onChange={(val) => handleChange('eventType', val)}
                    style={{ width: '100%', marginBottom: 16 }}
                  >
                    {EVENT_TYPES.map((type) => (
                      <Option key={type} value={type}>{type}</Option>
                    ))}
                  </Select>
                  <Button
                    block
                    size="large"
                    type="primary"
                    onClick={() => onApply('type_filter')}
                    disabled={!filters.eventType}
                  >
                    Filter By Type
                  </Button>
                </div>
              </div>
            )}

            {filterCategory === 'entity' && (
              <div className="filter-group-sec active-category">
                <div className="group-content">
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <Select
                      size="large"
                      placeholder="Entity Type"
                      value={filters.entityType || undefined}
                      onChange={(val) => handleChange('entityType', val)}
                      style={{ flex: 1 }}
                    >
                      {ENTITY_TYPES.map((type) => (
                        <Option key={type} value={type}>{type}</Option>
                      ))}
                    </Select>
                    <Input
                      size="large"
                      placeholder="Entity ID"
                      value={filters.entityId}
                      onChange={(e) => handleChange('entityId', e.target.value)}
                      style={{ flex: 1.5 }}
                    />
                  </div>
                  <Button
                    block
                    size="large"
                    type="primary"
                    onClick={() => onApply('entity_filter')}
                    disabled={!filters.entityType || !filters.entityId}
                  >
                    Filter By Entity
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="filter-panel-footer">
            <Button icon={<CloseOutlined />} onClick={() => setActivePanel(null)}>
              Close Filters
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailFilters;
